import React, { useState, useRef, useEffect, useMemo } from "react";
import DropdownWrapper from "../dropdown/DropdownWrapper";
import LocationDropdown from "./LocationDropdown";
import { ILocations, IRegionAndState } from "../../types/locations";
import useCheckShadow from "../../hooks/useCheckShadow";
import useDebounce from "../../hooks/useDebounce";
import Chip from "../Chip";
import { fetchMasterDataApi } from "../../apis/apis";
import { getShortLocationLabel } from "../../utils/utils";

interface IProps {
  middleware?: string;
  userDetails: any;
  setSelectedLocations: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLocations: string[];
  setIncludingRelocating: React.Dispatch<React.SetStateAction<boolean>>;
  includeRelocating: boolean;
  handleDropdownOpen: (open: boolean) => void;
  isActive?: boolean;
  hasActiveField?: boolean;
  handleFieldActive: (field: 'keywords' | 'location' | 'exp' | null) => void;
}

const Location: React.FC<IProps> = ({
  middleware = "",
  userDetails,
  setSelectedLocations,
  selectedLocations,
  setIncludingRelocating,
  includeRelocating,
  handleDropdownOpen,
  isActive = false,
  hasActiveField = false,
  handleFieldActive,
}) => {
  const [locations, setLocations] = useState<ILocations | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<IRegionAndState[]>([]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const { showLeftShadow, showRightShadow } = useCheckShadow({
    ref: chipsRef,
    scrollBasedOn: [selectedLocations?.length],
  });

  const filteredLocations = useMemo(() => {
    if (!locations || !debouncedSearchTerm.trim()) {
      return locations;
    }

    const searchLower = debouncedSearchTerm.toLowerCase().trim();

    // Filter top cities
    const filteredTopCities = locations.topCities.filter((city) =>
      city.value.toLowerCase().includes(searchLower)
    );

    // Filter region and states - check both region name and cities within regions
    const filteredRegionAndStates = locations.regionAndStates
      .map((region) => {
        const regionMatches = region.name.toLowerCase().includes(searchLower);
        return {
          ...region,
          cities: regionMatches 
            ? region.cities // If region matches, keep all cities
            : region.cities.filter((city) =>
                city.value.toLowerCase().includes(searchLower)
              ),
        };
      })
      .filter(
        (region) =>
          region.name.toLowerCase().includes(searchLower) ||
          region.cities.length > 0
      );

    // Filter international locations
    const filteredInternationalLocations =
      locations.internationalLocations.filter((loc) =>
        loc.value.toLowerCase().includes(searchLower)
      );

    return {
      topCities: filteredTopCities,
      regionAndStates: filteredRegionAndStates,
      internationalLocations: filteredInternationalLocations,
    };
  }, [locations, debouncedSearchTerm]);

  const handleSelect = (
    selected: string[] | string,
    region?: IRegionAndState
  ) => {
    setSelectedLocations(selected as string[]);
    if (region) {
      // remove if region is already selected
      if (selectedRegions.map((item) => item.id).includes(region.id)) {
        const updated = selectedRegions.filter((item) => item.id !== region.id);
        setSelectedRegions(updated);
        return;
      }
      // add region to selectedRegion
      setSelectedRegions((prev) => [...prev, region]);
    }
  };

  const toggleRelocating = () => {
    setIncludingRelocating((prev) => !prev);
  };

  const handleCloseDropdown = () => {
    setShowDropdown(false);
    handleFieldActive(null);
  };

  const handleDeleteLocation = (locationToDelete: string) => {
    // Remove the location from selectedLocations
    const updatedLocations = selectedLocations.filter(
      (loc) => loc !== locationToDelete
    );
    setSelectedLocations(updatedLocations);

    // Find and remove any regions that contain this city
    if (locations) {
      const regionsToRemove = selectedRegions.filter((region) => {
        // Check if the region contains the city to be deleted
        return region.cities.some((city) => city.value === locationToDelete);
      });

      if (regionsToRemove.length > 0) {
        const updatedRegions = selectedRegions.filter(
          (region) =>
            !regionsToRemove.some(
              (regionToRemove) => regionToRemove.id === region.id
            )
        );
        setSelectedRegions(updatedRegions);
      }
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    handleDropdownOpen(true);
    handleFieldActive('location');
    // setSearchTerm("");
  };

  useEffect(() => {
    const fetchMasterData = async () => {
      const result = await fetchMasterDataApi({ middleware, userDetails });
      if (result) setLocations(result);
    };
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (!searchTerm && selectedRegions.length > 0) {
      // remove the regions if any region  has not selected all its cities
      const updatedRegions = selectedRegions.filter((region) =>
        locations?.regionAndStates
          ?.filter((item) => item.id === region.id)?.[0]
          ?.cities.every((city) => selectedLocations.includes(city.value))
      );
      setSelectedRegions(updatedRegions);
    }
  }, [searchTerm]);

  useEffect(()=>{
    if(searchTerm && !showDropdown){
      setSearchTerm("");
    }
  },[searchTerm, showDropdown])

  const className = `qs_search_input_sec location${isActive ? ' active' : hasActiveField ? ' inactive' : ''}`;

  return (
    <div className={className} ref={wrapperRef}>
      <div className="qsSprite icons location" style={{ flexShrink: 0 }} />

      <div className="qs_search_chip_container_wrapper">
        {showLeftShadow && <div className="qs_overflow_shadow left" />}

        <div className="qs_search_chip_container" ref={chipsRef}>
          {!!selectedLocations?.length && (
            <div className="qs_search_chips">
              {selectedLocations?.map((loc, index) => (
                <Chip
                  key={`${loc}_${index}`}
                  chipDetails={{ id: loc, text: getShortLocationLabel(loc) }}
                  onDelete={() => handleDeleteLocation(loc)}
                />
              ))}
            </div>
          )}
          <div className="qs_search_input_container location">
            <input
              id="qs_search_input_loc"
              className="qs_search_input"
              type="text"
              placeholder="Location"
              value={searchTerm}
              autoComplete="off"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleFieldActive('location');
              }}
              onFocus={handleInputFocus}
              onBlur={() => {
                if (!showDropdown|| !filteredLocations) {
                  handleFieldActive(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !searchTerm) {
                  e.preventDefault();
                  setSelectedLocations((prev) => prev.slice(0, -1));

                  const activeGroup = selectedRegions.find((region) =>
                    region.cities.some(
                      (city) =>
                        city.value ===
                        selectedLocations[selectedLocations.length - 1]
                    )
                  );
                  if (activeGroup) {
                    setSelectedRegions((prev) =>
                      prev.filter((region) => region.id !== activeGroup.id)
                    );
                  }
                }
              }}
            />
          </div>
        </div>
        {showRightShadow && <div className="qs_overflow_shadow right" />}
      </div>

      {showDropdown && filteredLocations && (
        <DropdownWrapper
          classNames="qs_loc_dropdown"
          isOpen={showDropdown}
          handleClose={handleCloseDropdown}
          handleFieldActive={handleFieldActive}
          containerRef={wrapperRef}
        >
        <LocationDropdown
            items={filteredLocations}
            query={debouncedSearchTerm}
            onSelect={handleSelect}
            selectedRegions={selectedRegions}
            selectedLocations={selectedLocations}
            toggleRelocating={toggleRelocating}
            includeRelocating={includeRelocating}
            setSelectedRegions={setSelectedRegions}
            searchTerm={searchTerm}
          />
        </DropdownWrapper>
      )}
    </div>
  );
};

export default React.memo(Location);
